import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.#interval !== undefined) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

/**
 * Continuous announcements. Only the original message group enters the tab order
 * and accessibility tree; visual copies stay clickable with a pointer.
 * @typedef {object} MarqueeRefs
 * @property {HTMLElement} track
 * @property {HTMLElement} group
 * @extends {Component<MarqueeRefs>}
 */
class AnnouncementMarquee extends Component {
  requiredRefs = ['track', 'group'];
  #resizeObserver;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#configure();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#configure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#generation += 1;
    this.#resizeObserver?.disconnect();
  }

  #configure() {
    const generation = ++this.#generation;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#repeat);
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(this.refs.group);
    this.#repeat();
    document.fonts.ready.then(() => {
      if (this.isConnected && generation === this.#generation) this.#repeat();
    });
  }

  #repeat = () => {
    const { track, group } = this.refs;
    const width = group.getBoundingClientRect().width;
    if (!width) return;

    const copies = Math.ceil(this.clientWidth / width) + 1;
    // Read responsive speed before adding/removing groups. A computed-style
    // read after those DOM writes would force another style/layout pass.
    const speedRatio = Number(getComputedStyle(this).getPropertyValue('--pd-announcement-speed-ratio')) || 1;
    const pixelsPerSecond = Math.max(1, (Number(this.dataset.speed) || 100) * speedRatio);
    const duration = `${width / pixelsPerSecond}s`;
    while (track.children.length > copies) track.lastElementChild?.remove();
    while (track.children.length < copies) {
      const clone = /** @type {HTMLElement} */ (group.cloneNode(true));
      clone.removeAttribute('ref');
      clone.setAttribute('aria-hidden', 'true');
      for (const element of clone.querySelectorAll('[ref], [data-shopify-editor-block], [id]')) {
        element.removeAttribute('ref');
        element.removeAttribute('data-shopify-editor-block');
        element.removeAttribute('id');
      }
      for (const link of clone.querySelectorAll('a')) link.setAttribute('tabindex', '-1');
      track.appendChild(clone);
    }

    // Keep the mobile pace in proportion to its tighter separator spacing.
    if (this.style.getPropertyValue('--pd-announcement-duration') !== duration) {
      this.style.setProperty('--pd-announcement-duration', duration);
    }
    if (!this.hasAttribute('data-ready')) this.setAttribute('data-ready', '');
  };

}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}

/** Absolute-time countdown; each marquee copy stays current without shared IDs or refs. */
class AnnouncementCountdown extends Component {
  /** @type {number|undefined} */
  #timer;
  #deadline = NaN;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#timer);
  }

  #start() {
    clearTimeout(this.#timer);
    this.#deadline = Date.parse(this.dataset.end || '');
    if (Number.isFinite(this.#deadline)) this.#tick();
  }

  #tick = () => {
    const labelElement = this.querySelector('[data-countdown-label]');
    const timeElement = this.querySelector('[data-countdown-time]');
    if (!labelElement || !(timeElement instanceof HTMLElement)) return;

    // Use the deadline on every tick so background tabs cannot make the clock drift.
    const remaining = Math.max(0, Math.ceil((this.#deadline - Date.now()) / 1000));
    const parts = {
      days: Math.floor(remaining / 86400),
      hours: Math.floor((remaining % 86400) / 3600),
      minutes: Math.floor((remaining % 3600) / 60),
      seconds: remaining % 60,
    };
    const format = (template = '') => template.replace(
      /\[(days|hours|minutes|seconds)\]/g,
      (_, unit) => String(parts[unit])
    );
    const text = (remaining ? this.dataset.prefix : this.dataset.expired) || '';
    const label = remaining
      ? `${this.dataset.prefix} ${format(this.dataset.accessibleFormat)}`
      : this.dataset.expired || '';

    if (labelElement.textContent !== text) labelElement.textContent = text;
    timeElement.hidden = !remaining;
    for (const element of timeElement.querySelectorAll('[data-countdown-unit]')) {
      const unit = element.getAttribute('data-countdown-unit');
      if (!unit || !(unit in parts)) continue;
      const digits = String(parts[unit]).padStart(2, '0');
      if (element.textContent !== digits) element.textContent = digits;
    }
    this.setAttribute('aria-label', label);
    if (remaining) this.#timer = setTimeout(this.#tick, 1000 - (Date.now() % 1000));
  };
}

if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}
